// frontend/src/components/UbicacionSelector.jsx

import React, { useState, useEffect } from 'react';
import { ESTADOS_MEXICO, getCiudadesPorEstado, esOtraCiudad } from '../data/ciudadesMexico';
import axios from 'axios';
import { logger } from '../utils/logger';

/**
 * Componente para seleccionar ubicación (Estado + Ciudad)
 * Incluye validación cuando el usuario selecciona "Otra ciudad"
 * 
 * @param {Object} props
 * @param {string} props.estadoInicial - Estado seleccionado inicialmente
 * @param {string} props.ciudadInicial - Ciudad seleccionada inicialmente
 * @param {Function} props.onChange - Callback (estado, ciudad, valida)
 * @param {boolean} props.mostrarValidacion - Mostrar indicador de validación
 */
export default function UbicacionSelector({ 
  estadoInicial = '', 
  ciudadInicial = '',
  onChange,
  mostrarValidacion = true
}) {
  const [estado, setEstado] = useState(estadoInicial);
  const [ciudad, setCiudad] = useState(ciudadInicial);
  const [ciudadPersonalizada, setCiudadPersonalizada] = useState('');
  const [validando, setValidando] = useState(false);
  const [esValida, setEsValida] = useState(null); // null | true | false
  
  const ciudadesDisponibles = estado ? getCiudadesPorEstado(estado) : [];
  const mostrarCampoOtra = esOtraCiudad(ciudad);

  // ✅ Sincronizar con props cuando cambian (para cargar datos existentes)
  useEffect(() => {
    if (estadoInicial !== estado) {
      setEstado(estadoInicial);
    }
    if (ciudadInicial !== ciudad) {
      setCiudad(ciudadInicial);
    }
  }, [estadoInicial, ciudadInicial]);

  // Handler cuando cambia el estado
  const handleEstadoChange = (e) => {
    const nuevoEstado = e.target.value;
    setEstado(nuevoEstado);
    setCiudad(''); // Reset ciudad
    setCiudadPersonalizada('');
    setEsValida(null);
    
    // Notificar al padre
    if (onChange) {
      onChange(nuevoEstado, '', false);
    }
  };

  // Handler cuando cambia la ciudad (dropdown)
  const handleCiudadChange = (e) => {
    const nuevaCiudad = e.target.value;
    setCiudad(nuevaCiudad);
    setCiudadPersonalizada('');
    
    // Si no es "Otra ciudad", es válida automáticamente
    if (!esOtraCiudad(nuevaCiudad)) {
      setEsValida(true);
      if (onChange) {
        onChange(estado, nuevaCiudad, true);
      }
    } else {
      setEsValida(null);
      if (onChange) {
        onChange(estado, '', false);
      }
    }
  };

  // Handler cuando escribe ciudad personalizada
  const handleCiudadPersonalizadaChange = (e) => {
    const valor = e.target.value;
    setCiudadPersonalizada(valor);
    setEsValida(null); // Reset validación
  };

  // Validar ciudad personalizada con OpenWeatherMap
  const validarCiudadPersonalizada = async () => {
    if (!ciudadPersonalizada.trim() || !estado) return;

    setValidando(true);
    setEsValida(null);

    try {
      const token = localStorage.getItem('nfcToken');
      const requestData = {
        ciudad: ciudadPersonalizada,
        estado: estado,
        pais: 'MX'
      };

      logger.debug('Clima - Validando ciudad:', requestData);

      const response = await axios.post('/api/clima/validar', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      logger.debug('Clima - Respuesta validación:', response.data);

      const valida = response.data.valida;
      setEsValida(valida);

      if (onChange) {
        onChange(estado, ciudadPersonalizada, valida);
      }
    } catch (error) {
      logger.error('Clima - Error validando ciudad:', error);
      setEsValida(false);
      if (onChange) {
        onChange(estado, ciudadPersonalizada, false);
      }
    } finally {
      setValidando(false);
    }
  };

  // Validar automáticamente después de 1 segundo de dejar de escribir
  useEffect(() => {
    if (!mostrarCampoOtra || !ciudadPersonalizada) return;
    
    const timer = setTimeout(() => {
      validarCiudadPersonalizada();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [ciudadPersonalizada, estado]);

  return (
    <div className="ubicacion-selector">
      {/* Selector de Estado */}
      <div className="mb-3">
        <label className="form-label" style={{ fontWeight: 600 }}>
          <span className="text-danger">* </span>Estado:
        </label>
        <select 
          className="form-select"
          value={estado}
          onChange={handleEstadoChange}
          style={{
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-2) var(--space-3)'
          }}
        >
          <option value="">-- Selecciona un estado --</option>
          {ESTADOS_MEXICO.map(est => (
            <option key={est} value={est}>{est}</option>
          ))}
        </select>
      </div>

      {/* Selector de Ciudad (solo si hay estado) */}
      {estado && (
        <div className="mb-3">
          <label className="form-label" style={{ fontWeight: 600 }}>
            <span className="text-danger">* </span>Ciudad:
          </label>
          <select 
            className="form-select"
            value={ciudad}
            onChange={handleCiudadChange}
            disabled={!estado}
            style={{
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-2) var(--space-3)'
            }}
          >
            <option value="">-- Selecciona una ciudad --</option>
            {ciudadesDisponibles.map(ciu => (
              <option key={ciu} value={ciu}>{ciu}</option>
            ))}
          </select>
        </div>
      )}

      {/* Campo de texto para "Otra ciudad" */}
      {mostrarCampoOtra && (
        <div className="mb-3">
          <label className="form-label" style={{ fontWeight: 600 }}>
            Escribe tu ciudad:
          </label>
          <div className="input-group">
            <input
              type="text"
              className={`form-control ${
                esValida === true ? 'is-valid' : 
                esValida === false ? 'is-invalid' : ''
              }`}
              value={ciudadPersonalizada}
              onChange={handleCiudadPersonalizadaChange}
              placeholder="Ej: Boca del Río"
              disabled={validando}
              style={{
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-2) var(--space-3)'
              }}
            />
            {validando && (
              <span className="input-group-text">
                <span className="spinner-border spinner-border-sm"></span>
              </span>
            )}
          </div>
          
          {/* Feedback de validación */}
          {mostrarValidacion && (
            <>
              {esValida === true && (
                <div className="text-success mt-2" style={{ fontSize: '0.875rem' }}>
                  ✅ Ciudad encontrada y válida
                </div>
              )}
              {esValida === false && (
                <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                  ❌ Ciudad no encontrada. Verifica el nombre.
                </div>
              )}
              {validando && (
                <div className="text-muted mt-2" style={{ fontSize: '0.875rem' }}>
                  🔍 Validando ciudad...
                </div>
              )}
            </>
          )}
          
          <small className="text-muted d-block mt-1">
            Escribe el nombre exacto de tu ciudad. Se validará automáticamente.
          </small>
        </div>
      )}

      {/* Resumen de selección */}
      {estado && ciudad && !mostrarCampoOtra && (
        <div 
          className="alert alert-info"
          style={{
            fontSize: '0.875rem',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)'
          }}
        >
          📍 <strong>Ubicación:</strong> {ciudad}, {estado}, México
        </div>
      )}
      
      {estado && mostrarCampoOtra && ciudadPersonalizada && esValida === true && (
        <div 
          className="alert alert-success"
          style={{
            fontSize: '0.875rem',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)'
          }}
        >
          📍 <strong>Ubicación:</strong> {ciudadPersonalizada}, {estado}, México
        </div>
      )}
    </div>
  );
}